/**
 * constants/ncfFramework.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for the frontend's NCF-FS 2022 + Panchakosha
 * reference data. Before this file existed, the same data was copy-pasted
 * independently into ProgressTracker.jsx (DOMAINS, LEVELS, KOSHA_DOMAIN_MAP)
 * and again into ResearchDashboard.jsx (INDICATORS_BY_DOMAIN, KOSHA_LABELS,
 * KOSHA_COLORS) — any future change (a relabeled indicator, a new kosha
 * subtitle) had to be made in multiple places and would silently drift if
 * one copy was missed. Import from here instead of redeclaring.
 *
 * NOTE — this does NOT replace models/Child.js's Mongoose `disabilities`
 * enum on the backend. That's a separate runtime (Node/Mongoose vs the
 * Vite/React bundle this file lives in), so the two can't literally share
 * one array unless this becomes a real monorepo shared package. DISABILITIES
 * below must still be kept in sync with Child.js by hand if it ever changes
 * — this file only collapses the *frontend* duplicates.
 *
 * Usage:
 *   import { DOMAINS, LEVELS, PANCHAKOSHA, DISABILITIES } from '@/constants/ncfFramework';
 */

// ── Disability profile — mirrors Child.js's Mongoose enum ──────────────────
export const DISABILITIES = [
    'Hearing Impairment',
    'Autism Spectrum Disorder',
    'Down Syndrome',
    'Cerebral Palsy',
    'Intellectual Disability',
    'Visual Impairment',
    'Speech & Language Disorder',
    'Global Developmental Delay',
    'Multiple Disabilities',
    'Other',
  ];
  
  // ── NCF-FS 2022 rating levels — used by Progress Tracker and anywhere else
  // that reads DevelopmentRecord history. score is the 0–3 rubric value used
  // in the domain-percentage formula (Σ(levels)÷(attempted×3)×100).
  export const LEVELS = [
    { id: 'not-observed', label: 'Not Observed', short: 'N/O', color: '#9CA3AF', bg: '#F9FAFB', score: 0 },
    { id: 'emerging',     label: 'Emerging',     short: 'E',   color: '#EA580C', bg: '#FFF7ED', score: 1 },
    { id: 'developing',   label: 'Developing',   short: 'D',   color: '#B45309', bg: '#FFFBEB', score: 2 },
    { id: 'achieved',     label: 'Achieved',     short: 'A',   color: '#16A34A', bg: '#F0FDF4', score: 3 },
  ];
  export const LEVEL_SCORE = Object.fromEntries(LEVELS.map(l => [l.id, l.score]));
  
  // ── Five NCF-FS domains, 8 indicators each — canonical copy ─────────────────
  export const DOMAINS = [
    {
      id: 'physical-motor', label: 'Physical & Motor', shortLabel: 'Physical',
      icon: '🏃', color: '#16A34A', bg: '#F0FDF4', border: '#86EFAC',
      indicators: [
        { id: 'pm1', label: 'Holds pencil/crayon with control',      category: 'Fine Motor' },
        { id: 'pm2', label: 'Cuts along a line with scissors',        category: 'Fine Motor' },
        { id: 'pm3', label: 'Strings beads or laces shoes',           category: 'Fine Motor' },
        { id: 'pm4', label: 'Draws basic shapes (circle, square)',    category: 'Fine Motor' },
        { id: 'pm5', label: 'Walks, runs, jumps with coordination',   category: 'Gross Motor' },
        { id: 'pm6', label: 'Catches and throws a ball',              category: 'Gross Motor' },
        { id: 'pm7', label: 'Balances on one foot briefly',           category: 'Gross Motor' },
        { id: 'pm8', label: 'Climbs, crawls, rolls with confidence',  category: 'Gross Motor' },
      ],
    },
    {
      id: 'socio-emotional', label: 'Socio-Emotional', shortLabel: 'Social',
      icon: '💛', color: '#EA580C', bg: '#FFF7ED', border: '#FDBA74',
      indicators: [
        { id: 'se1', label: 'Identifies and names basic emotions',        category: 'Self-Awareness' },
        { id: 'se2', label: 'Regulates strong emotions with support',     category: 'Self-Regulation' },
        { id: 'se3', label: 'Shares and takes turns with peers',          category: 'Peer Interaction' },
        { id: 'se4', label: 'Shows empathy toward others',                category: 'Empathy' },
        { id: 'se5', label: 'Separates from caregiver without distress',  category: 'Independence' },
        { id: 'se6', label: 'Participates in group activities',           category: 'Peer Interaction' },
        { id: 'se7', label: 'Follows simple classroom rules',             category: 'Self-Regulation' },
        { id: 'se8', label: 'Expresses needs and wants clearly',          category: 'Self-Awareness' },
      ],
    },
    {
      id: 'cognitive', label: 'Cognitive', shortLabel: 'Cognitive',
      icon: '🧠', color: '#9333EA', bg: '#F5F3FF', border: '#C4B5FD',
      indicators: [
        { id: 'cg1', label: 'Sorts objects by color, shape, size',     category: 'Classification' },
        { id: 'cg2', label: 'Identifies patterns and continues them',  category: 'Patterns' },
        { id: 'cg3', label: 'Solves simple cause-and-effect problems', category: 'Reasoning' },
        { id: 'cg4', label: 'Matches and categorizes objects',         category: 'Classification' },
        { id: 'cg5', label: 'Sequences events in correct order',       category: 'Sequencing' },
        { id: 'cg6', label: 'Demonstrates memory for recent events',   category: 'Memory' },
        { id: 'cg7', label: 'Completes simple puzzles (4–8 pieces)',   category: 'Reasoning' },
        { id: 'cg8', label: 'Shows curiosity and asks questions',      category: 'Inquiry' },
      ],
    },
    {
      id: 'language-literacy', label: 'Language & Literacy', shortLabel: 'Language',
      icon: '📚', color: '#2563EB', bg: '#EFF6FF', border: '#93C5FD',
      indicators: [
        { id: 'll1', label: 'Listens and responds to simple instructions',  category: 'Comprehension' },
        { id: 'll2', label: 'Speaks in 3–5 word sentences',                category: 'Expression' },
        { id: 'll3', label: 'Recognizes and names letters (mother tongue)', category: 'Literacy' },
        { id: 'll4', label: 'Identifies beginning sounds of words',         category: 'Phonological' },
        { id: 'll5', label: 'Retells a story with key details',             category: 'Comprehension' },
        { id: 'll6', label: 'Shows book-handling behaviour (left-right)',   category: 'Literacy' },
        { id: 'll7', label: 'Engages in conversation with turns',           category: 'Expression' },
        { id: 'll8', label: 'Recognizes own name in print',                 category: 'Literacy' },
      ],
    },
    {
      id: 'numeracy', label: 'Numeracy', shortLabel: 'Numeracy',
      icon: '🔢', color: '#0891B2', bg: '#ECFEFF', border: '#67E8F9',
      indicators: [
        { id: 'nm1', label: 'Counts objects 1–10 with 1-to-1 correspondence', category: 'Counting' },
        { id: 'nm2', label: 'Recognizes numerals 1–10',                       category: 'Number Recognition' },
        { id: 'nm3', label: 'Compares quantities (more, less, equal)',         category: 'Comparison' },
        { id: 'nm4', label: 'Identifies basic shapes in environment',          category: 'Geometry' },
        { id: 'nm5', label: 'Uses positional language (above, below, beside)', category: 'Spatial' },
        { id: 'nm6', label: 'Completes simple AB/ABC patterns',               category: 'Patterns' },
        { id: 'nm7', label: 'Understands concept of addition (adding more)',   category: 'Operations' },
        { id: 'nm8', label: 'Sorts and groups by number',                      category: 'Classification' },
      ],
    },
  ];
  export const DOMAIN_IDS = DOMAINS.map(d => d.id);
  // Convenience lookup: domainId -> that domain's indicators array.
  export const INDICATORS_BY_DOMAIN = Object.fromEntries(DOMAINS.map(d => [d.id, d.indicators]));
  
  // ── Panchakosha — five koshas, with the approximate NCF domain each maps to
  // (anandamaya has no NCF equivalent, per ProgressTracker.jsx's ReadinessPanel
  // mapping notes — kept here as ncfDomainId: null rather than guessing one).
  export const PANCHAKOSHA = [
    { id: 'annamaya',    label: 'Annamaya',    subtitle: 'Physical Body',           icon: '🏃', color: '#0E8A7A', ncfDomainId: 'physical-motor'    },
    { id: 'pranamaya',   label: 'Pranamaya',   subtitle: 'Vital Energy & Emotions', icon: '💛', color: '#C2620A', ncfDomainId: 'socio-emotional'   },
    { id: 'manomaya',    label: 'Manomaya',    subtitle: 'Mind & Perception',       icon: '🧠', color: '#6E3EA6', ncfDomainId: 'cognitive'         },
    { id: 'vijnanamaya', label: 'Vijnanamaya', subtitle: 'Intellect & Language',    icon: '📚', color: '#1A4F7A', ncfDomainId: 'language-literacy' },
    { id: 'anandamaya',  label: 'Anandamaya',  subtitle: 'Joy, Play & Creativity',  icon: '🎨', color: '#B03060', ncfDomainId: null                },
  ];
  export const PANCHAKOSHA_IDS    = PANCHAKOSHA.map(k => k.id);
  export const KOSHA_LABEL_BY_ID  = Object.fromEntries(PANCHAKOSHA.map(k => [k.id, k.label]));
  export const KOSHA_COLOR_BY_ID  = Object.fromEntries(PANCHAKOSHA.map(k => [k.id, k.color]));